/**
 * StripeControlCenter.jsx – Phase 1a
 * Superadmin-only: Read-Only Stripe Control Center
 * 
 * Tabs Phase 1a: Übersicht | Kunden | Rechnungen | Zahlungen | Synchronisation
 * Tabs Phase 1b+ (Platzhalter): Standorte | Pakete | Rabatte | Rückerstattungen | Einstellungen
 */
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../../components/DashboardLayout';
import StripeOverview from './StripeOverview';
import StripeCustomers from './StripeCustomers';
import StripeInvoices from './StripeInvoices';
import StripePayments from './StripePayments';
import StripeSync from './StripeSync';
import StripePackages from './StripePackages';
import {
  BarChart3, Users, FileText, CreditCard, RefreshCw,
  MapPin, Package, Tag, RotateCcw, Settings, Zap,
} from 'lucide-react';

const TABS_ACTIVE = [
  { id: 'overview',   label: 'Übersicht',        icon: BarChart3,  phase: '1a' },
  { id: 'customers',  label: 'Kunden',            icon: Users,      phase: '1a' },
  { id: 'invoices',   label: 'Rechnungen',        icon: FileText,   phase: '1a' },
  { id: 'payments',   label: 'Zahlungen',         icon: CreditCard, phase: '1a' },
  { id: 'sync',       label: 'Synchronisation',   icon: RefreshCw,  phase: '1a' },
  { id: 'packages',   label: 'Pakete & Preise',   icon: Package,    phase: '1b' },
];

const TABS_PLANNED = [
  { id: 'locations',    label: 'Standorte & Abos', icon: MapPin,      phase: '2'  },
  { id: 'discounts',    label: 'Rabatte',           icon: Tag,         phase: '2'  },
  { id: 'refunds',      label: 'Rückerstattungen',  icon: RotateCcw,   phase: '2'  },
  { id: 'settings',     label: 'Einstellungen',     icon: Settings,    phase: '2'  },
];

export default function StripeControlCenter() {
  const [activeTab, setActiveTab] = useState('overview');
  const [stripeMode, setStripeMode] = useState(null); // 'test' | 'live' | null (loading)

  useEffect(() => {
    // Modus IMMER vom Backend holen – niemals aus Frontend-Keys
    // Backend liest sk_live_* vs sk_test_* aus der Serverumgebung
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/superadmin/stripe-cc/sync/status`, { headers })
      .then(r => r.json())
      .then(d => setStripeMode(d.mode || 'test'))
      .catch(() => setStripeMode('test')); // Fallback: Test anzeigen wenn unbekannt
  }, []);

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                  <Zap className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                    Stripe Control Center
                  </h1>
                  <p className="text-xs text-muted-foreground hidden sm:block">
                    Zahlungen, Abonnements und Kunden verwalten
                  </p>
                </div>
              </div>
              {/* Modus-Badge */}
              {stripeMode === 'test' && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
                  <span className="text-xs font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wide">
                    TESTMODUS – keine echten Zahlungen
                  </span>
                </div>
              )}
              {stripeMode === 'live' && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg">
                  <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  <span className="text-xs font-bold text-green-800 dark:text-green-300 uppercase tracking-wide">
                    Live-Modus
                  </span>
                </div>
              )}
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-0.5 mt-4 overflow-x-auto pb-px">
              {TABS_ACTIVE.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`stripe-tab-${tab.id}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap transition-all border-b-2 ${
                      isActive
                        ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
              {/* Platzhalter-Tabs (Phase 1b+) */}
              {TABS_PLANNED.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    disabled
                    title={`Kommt in Phase ${tab.phase}`}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap opacity-35 cursor-not-allowed border-b-2 border-transparent text-muted-foreground"
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="hidden sm:inline text-[9px] bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded font-mono">
                      {tab.phase}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          {activeTab === 'overview'  && <StripeOverview />}
          {activeTab === 'customers' && <StripeCustomers />}
          {activeTab === 'invoices'  && <StripeInvoices />}
          {activeTab === 'payments'  && <StripePayments />}
          {activeTab === 'sync'      && <StripeSync />}
          {activeTab === 'packages'  && <StripePackages />}
        </div>
      </div>
    </DashboardLayout>
  );
}
